import { AssociationCategoryIdsToTypes, AssociationCategoryTypesToIds, HUBSPOT_DEFINED } from 'customer-data-objects/associations/AssociationCategoryTypes';
import { Map as ImmutableMap } from 'immutable';
// @ts-expect-error untyped
import { getDefaultGettersByObjectTypeId } from 'reference-resolvers/formatters/formatCustomObjects';
import { getAssociationById, getAssociationByInverseId, getWildcardAssociation } from 'customer-data-filters-ui/strategy/InboundDbListSegStrategyUtils';
import getIn from 'transmute/getIn';
import { ASSOCIATION_DIRECTION } from '../filterQueryFormat/associations/AssociationUtilities';
import { HUBSPOT_DEFINED_TYPE, USER_DEFINED_TYPE } from '../filterQueryFormat/associations/AssociationCategoryTypes';
import { TASK_TEMPLATE_TYPE_ID } from '../constants/objectTypeIds';
import { CONVERSATION_SESSION_TYPE_ID, SALES_TASK_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';

// '0' | '1' | '2'

export const getAssociationInfoFromPropertySuffix = propertySuffix => {
  if (!propertySuffix) {
    return {};
  }
  const [associationCategoryId, associationId] = propertySuffix.split('-');
  return {
    associationCategoryId: associationCategoryId,
    associationCategory: AssociationCategoryIdsToTypes[associationCategoryId],
    associationId: Number(associationId)
  };
};
export const getAssociationInfoFromAssociationValue = associationValue => {
  if (!associationValue) {
    return {};
  }
  const [associationId, associationCategory] = associationValue.split('--');
  return {
    associationCategory: associationCategory,
    associationCategoryId: AssociationCategoryTypesToIds[associationCategory],
    associationId: Number(associationId)
  };
};
const getAssociationCategoryId = associationDefinition => {
  if (associationDefinition.category && AssociationCategoryTypesToIds[associationDefinition.category]) {
    return AssociationCategoryTypesToIds[associationDefinition.category];
  }
  return null;
};
export const getAssociationUniqueIdentifier = (associationDefinition, associationDirection = ASSOCIATION_DIRECTION.STANDARD) => {
  const prefix = getAssociationCategoryId(associationDefinition);
  const id = associationDirection === ASSOCIATION_DIRECTION.INVERSE ? associationDefinition.get('inverseId') : associationDefinition.get('id');
  if (prefix !== null) {
    return `${prefix}-${id}`;
  }
  return null;
};
export const getPropertySuffixFromAssociationValue = associationValue => {
  const [id, category] = associationValue.split('--');
  if (typeof category !== 'undefined' && typeof AssociationCategoryTypesToIds[category] !== 'undefined' && !isNaN(Number(id))) {
    const prefix = AssociationCategoryTypesToIds[category];
    return `${prefix}-${Number(id)}`;
  }
  return null;
};
export const getAssociationValueFromPropertySuffix = propertySuffix => {
  const [prefix, id] = propertySuffix.split('-');
  if (typeof prefix !== 'undefined' && typeof AssociationCategoryIdsToTypes[prefix] !== 'undefined' && !isNaN(Number(id))) {
    return `${Number(id)}--${AssociationCategoryIdsToTypes[prefix]}`;
  }
  return null;
};
export const getObjectsAssociationOptionsFlatMap = associationOptions => {
  return associationOptions.flatMap(assn => 'options' in assn ? assn.options : assn);
};
export const getAssociationOption = (associationOptions, associationId, associationCategory, associationDirection = ASSOCIATION_DIRECTION.STANDARD) => {
  if (!associationOptions) {
    return;
  }
  const associationsFlatMap = getObjectsAssociationOptionsFlatMap(associationOptions);
  if (typeof associationId !== 'undefined' && typeof associationCategory !== 'undefined') {
    const associationOption = associationsFlatMap.find(assn => {
      return associationDirection === ASSOCIATION_DIRECTION.STANDARD ? assn.id === associationId && getAssociationInfoFromAssociationValue(assn.value).associationCategory === associationCategory : assn.inverseId === associationId && getAssociationInfoFromAssociationValue(assn.value).associationCategory === associationCategory;
    });
    if (associationOption) {
      return associationOption;
    }
  }
  const wildcardAssociation = associationsFlatMap.find(assn => assn.isWildcard);
  return wildcardAssociation;
};
export const getAssociationOptionByValue = (associationOptions, value) => {
  if (associationOptions) {
    return getObjectsAssociationOptionsFlatMap(associationOptions).find(assn => assn.value === value);
  }
};

/**
 * Object type blocklists
 *
 * Objects covered here should stay in sync with those in the below file
 * https://git.hubteam.com/HubSpot/crm-index-data-lib/blob/master/crm-index-associations-lib/static/js/utils/isSupportedAssociation.ts
 */
const OBJECT_TYPE_BLOCKLIST = new Set([TASK_TEMPLATE_TYPE_ID, CONVERSATION_SESSION_TYPE_ID, SALES_TASK_TYPE_ID]);
const isObjectTypeSupported = objectTypeId => {
  return !OBJECT_TYPE_BLOCKLIST.has(objectTypeId);
};
const isFromSupported = associationDefinition => {
  return !!associationDefinition.fromObjectTypeId && isObjectTypeSupported(associationDefinition.fromObjectTypeId);
};
const isToSupported = associationDefinition => {
  return !!associationDefinition.toObjectTypeId && isObjectTypeSupported(associationDefinition.toObjectTypeId);
};

/**
 * Specific association between two objects blocklists
 *
 * Associations covered here should stay in sync with those in the below file
 * https://git.hubteam.com/HubSpot/crm-index-data-lib/blob/master/crm-index-associations-lib/static/js/utils/isSupportedAssociation.ts
 */
const CONTACT_TO_CONVERSATION_ASSOCIATION_TYPE_ID = '0-62';
const TICKET_TO_CONVERSATION_ASSOCIATION_TYPE_ID = '0-32';
const DEAL_TO_CONVERSATION_ASSOCIATION_TYPE_ID = '0-58';
const COMPANY_TO_CONVERSATION_ASSOCIATION_TYPE_ID = '0-60';
const PAYMENT_TO_DISCOUNT_ASSOCIATION_TYPE_ID = '0-428';
const ASSOCIATION_ID_BLOCKLIST = new Set([CONTACT_TO_CONVERSATION_ASSOCIATION_TYPE_ID, TICKET_TO_CONVERSATION_ASSOCIATION_TYPE_ID, DEAL_TO_CONVERSATION_ASSOCIATION_TYPE_ID, COMPANY_TO_CONVERSATION_ASSOCIATION_TYPE_ID, PAYMENT_TO_DISCOUNT_ASSOCIATION_TYPE_ID]);
const isSpecificAssociationBlocklisted = associationDefinition => {
  const associationTypeId = getAssociationUniqueIdentifier(associationDefinition);
  return associationTypeId !== null && ASSOCIATION_ID_BLOCKLIST.has(associationTypeId);
};
export const isSupportedAssociation = associationDefinition => {
  return !isSpecificAssociationBlocklisted(associationDefinition) && isFromSupported(associationDefinition) && isToSupported(associationDefinition);
};
const cachedCustomObjectReferenceResolvers = {};
export const getAssociatedRecordsUtils = (data, filterFamily) => {
  const {
    ASSOCIATION_DEFINITION = ImmutableMap(),
    CUSTOM_OBJECT_DEFINITION = ImmutableMap(),
    STANDARD_OBJECT_DEFINITION = ImmutableMap()
  } = data.toObject();
  const allAssociationDefinitions = ASSOCIATION_DEFINITION.get(filterFamily);
  const associationDefinitions = allAssociationDefinitions && allAssociationDefinitions
  //remove unsupported associations
  .map(associations => associations.filter(assn => isSupportedAssociation(assn)))
  //remove objects with no supported associations
  .filter(associations => {
    return associations.size > 0;
  });
  const associatedObjectTypeIds = associationDefinitions ? Object.keys(associationDefinitions.toObject()) : [];
  const getAssociationDefinition = (objectTypeId, associationId, associationCategory, associationDirection = ASSOCIATION_DIRECTION.STANDARD) => {
    const possibleAssociationDefinitions = associationDefinitions && associationDefinitions.get(objectTypeId);
    if (!possibleAssociationDefinitions) {
      return undefined;
    }
    if (possibleAssociationDefinitions.size === 1) {
      return Object.values(possibleAssociationDefinitions.toObject())[0];
    }
    if (associationId && associationCategory) {
      if (associationDirection === ASSOCIATION_DIRECTION.INVERSE) {
        return getAssociationByInverseId(Object.values(possibleAssociationDefinitions.toObject()), associationId, associationCategory);
      }
      return getAssociationById(Object.values(possibleAssociationDefinitions.toObject()), associationId, associationCategory);
    }
    return getWildcardAssociation(Object.values(possibleAssociationDefinitions.toObject()));
  };
  const getObjectDefinition = objectTypeId => {
    return STANDARD_OBJECT_DEFINITION.get(objectTypeId) || CUSTOM_OBJECT_DEFINITION.get(objectTypeId);
  };
  const getObjectTypeIdFromPropertySuffix = propertySuffix => {
    if (!propertySuffix) {
      return undefined;
    }
    const {
      associationCategory,
      associationCategoryId,
      associationId
    } = getAssociationInfoFromPropertySuffix(propertySuffix);
    return associatedObjectTypeIds.find(objectTypeId => {
      const associationDefinition = getAssociationDefinition(objectTypeId, associationId, associationCategory);
      if (associationDefinition) {
        const idMatches = associationDefinition.id === associationId;
        const prefixMatches = associationDefinition.category === HUBSPOT_DEFINED ? associationCategoryId === HUBSPOT_DEFINED_TYPE : associationCategoryId === USER_DEFINED_TYPE;
        return idMatches && prefixMatches;
      }
      return undefined;
    });
  };
  const getObjectTypeOptions = () => {
    return associatedObjectTypeIds.reduce((acc, objectTypeId) => {
      const objectDefinition = getObjectDefinition(objectTypeId);
      const disabled = objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.get('disabled');
      const singularForm = objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.get('singularForm');
      if (disabled || !singularForm) {
        return acc;
      }

      /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      return [...acc, {
        text: singularForm,
        value: objectTypeId
      }];
    }, []).sort(({
      text: text1
    }, {
      text: text2
    }) => text1.localeCompare(text2));
  };
  const getCustomObjectReferenceResolver = (resolver, objectTypeId) => {
    if (!objectTypeId) {
      return undefined;
    }
    const cachedCustomObjectReferenceResolver = cachedCustomObjectReferenceResolvers[objectTypeId];
    if (cachedCustomObjectReferenceResolver) {
      return cachedCustomObjectReferenceResolver;
    }
    const objectDefinition = getObjectDefinition(objectTypeId);
    const primaryDisplayLabelPropertyName = objectDefinition && objectDefinition.primaryDisplayLabelPropertyName ? objectDefinition.primaryDisplayLabelPropertyName : undefined;
    const {
      resolverFactory,
      resolversCache
    } = resolver();
    const formatterOptions = {
      getters: Object.assign({
        getLabel: ({
          objectId,
          properties
        }) => {
          return getIn([primaryDisplayLabelPropertyName, 'value'], properties) || `${objectId}`;
        }
      }, getDefaultGettersByObjectTypeId(objectTypeId))
    };
    const CustomObjectReferenceResolver = resolverFactory(objectTypeId, {
      formatterOptions
    });
    const customObjectReferenceResolver = CustomObjectReferenceResolver(resolversCache);
    cachedCustomObjectReferenceResolvers[objectTypeId] = objectDefinition && customObjectReferenceResolver;
    return customObjectReferenceResolver;
  };
  return {
    getAssociationDefinition,
    getObjectDefinition,
    getObjectTypeIdFromPropertySuffix,
    getObjectTypeOptions,
    getCustomObjectReferenceResolver
  };
};